import { cva, type VariantProps } from 'class-variance-authority';
import { type LucideIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@apps/web/lib/utils';

const emptyStateVariants = cva('text-center', {
    variants: {
        variant: {
            default:
                'flex min-h-40 flex-col items-center justify-center gap-2 p-8',
            compact:
                'flex min-h-24 flex-col items-center justify-center gap-1.5 p-4',
            chart: 'flex h-[320px] flex-col items-center justify-center gap-2 rounded-sm border border-dashed p-6',
            withCta:
                'flex min-h-40 flex-col items-center justify-center gap-2 p-8',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

type EmptyStateProps = React.ComponentProps<'div'> &
    VariantProps<typeof emptyStateVariants> & {
        readonly icon?: LucideIcon;
        readonly title: React.ReactNode;
        readonly description?: React.ReactNode;
        readonly action?: React.ReactNode;
    };

function EmptyState({
    action,
    className,
    description,
    icon: Icon,
    title,
    variant,
    ...props
}: EmptyStateProps) {
    const isLowEmphasis = variant === 'chart' || variant === 'compact';

    return (
        <div
            data-slot="empty-state"
            className={cn(emptyStateVariants({ variant }), className)}
            {...props}
        >
            {Icon ? (
                <Icon
                    aria-hidden="true"
                    className={cn(
                        'text-muted-foreground',
                        variant === 'compact' ? 'size-5' : 'size-8',
                    )}
                />
            ) : null}
            <div
                data-slot="empty-state-title"
                className={cn(
                    'text-sm',
                    isLowEmphasis
                        ? 'text-muted-foreground font-normal'
                        : 'text-foreground font-medium',
                )}
            >
                {title}
            </div>
            {description ? (
                <div
                    data-slot="empty-state-description"
                    className={cn(
                        'text-muted-foreground',
                        variant === 'compact' ? 'text-xs' : 'max-w-sm text-sm',
                    )}
                >
                    {description}
                </div>
            ) : null}
            {action ? (
                <div data-slot="empty-state-action" className="mt-2">
                    {action}
                </div>
            ) : null}
        </div>
    );
}

export { EmptyState, emptyStateVariants };
